import React, { useEffect, useState } from 'react';
import Styles from './styles';
import { PlayVideo } from '../../../../shared/components/Icons/PlayVideo';
import { SkipBackward } from '../../../../shared/components/Icons/SkipBackwards';
import { SkipForward } from '../../../../shared/components/Icons/SkipForward';
import { Fullscreen } from '../../../../shared/components/Icons/Fullscreen';
import { Time } from './Time';
import { Speed } from './Speed';
import { PlayBar } from './PlayBar';
import { EditionBar } from './EditionBar';
import { VolumePanel } from './VolumePanel';
import { Download } from '../../../../shared/components/Icons/Download';
import { useVideoPlayer } from '../../../hooks';
import { Pause } from '../../../../shared/components/Icons/Pause';
import { Scissors } from './Scissors';
import { Button } from 'antd';
import { SmileFilled, SmileOutlined } from '@ant-design/icons';
import { ReactionsController } from './ReactionsController';
import { Reactions } from './Reactions';

const {
  Container,
  LeftsideControls,
  RightsideControl,
  ControlsContainer,
  AllControls,
  VideoOverlay,
} = Styles;

interface Props {
  isEditable: boolean;
}

export const Controls: React.FC<Props> = ({ isEditable }) => {
  const {
    play,
    isPlaying,
    pause,
    skip,
    toggleFullscreen,
    fullscreen,
    download,
    markers,
    changeCropping,
    duration,
    togglePlay,
    isEditing: isDefaultEditing,
    customEditingMessage,
    notifyOn,
    blockActions,
    showReactions,
    setShowReactions,
    addReaction,
    reactions,
    allowReactions,
  } = useVideoPlayer();

  const [isReacting, setIsReacting] = useState(false);
  const [isEditing, setIsEditing] = useState(isDefaultEditing);
  const [tempMarkers, setTempMarkers] = useState(markers);

  useEffect(() => {
    setIsEditing(isDefaultEditing);
  }, [isDefaultEditing]);

  useEffect(() => {
    setTempMarkers(markers);
  }, [markers]);

  useEffect(() => {
    if (isReacting) {
      setIsReacting(false);
    }
  }, [reactions]);

  const handleReaction = (reaction: string) => {
    setIsReacting(true);
    addReaction(reaction);
  };

  const handleTimeChanged = (time: number[]) => {
    setTempMarkers([time[0], time[1]]);
  };

  const handleTimeCompleteChanged = (time: number[]) => {
    setTempMarkers([time[0], time[1]]);

    if (notifyOn === 'change') {
      const endTimeCropping = duration - time[1];
      changeCropping({ start: time[0], end: endTimeCropping });
    }
  };

  const markersEditionFinished = () => {
    const endTimeCropping = duration - tempMarkers[1];
    changeCropping({ start: tempMarkers[0], end: endTimeCropping });
    setIsEditing(false);
  };

  const markersEditionCanceled = () => {
    setTempMarkers([markers[0], markers[1]]);
    setIsEditing(false);
  };

  return (
    <Container id="controls-container" className={blockActions ? 'always-visible' : ''}>
      <VideoOverlay
        onClick={ev => {
          ev.preventDefault();
          ev.stopPropagation();
          togglePlay();
        }}
      />
      <AllControls>
        <div style={{ position: 'relative' }}>
          {allowReactions && showReactions && (
            <ReactionsController onReact={handleReaction} isReacting={isReacting} />
          )}
          {reactions.length > 0 && <Reactions reactions={reactions} duration={duration} />}
          {isEditing ? (
            <EditionBar
              duration={duration}
              markers={tempMarkers}
              message={customEditingMessage}
              onTimeChanged={handleTimeChanged}
              onTimeChangeComplete={handleTimeCompleteChanged}
            />
          ) : (
            <PlayBar marks={isEditable ? markers : null} />
          )}
        </div>
        <ControlsContainer>
          <LeftsideControls>
            {isPlaying ? <Pause onClick={() => pause()} /> : <PlayVideo onClick={() => play()} />}
            <SkipBackward onClick={() => skip(-15)} />
            <SkipForward onClick={() => skip(15)} />
            <Time />
            {!blockActions && allowReactions && (
              <span onClick={() => setShowReactions(!showReactions)} style={{ cursor: 'pointer' }}>
                {showReactions ? (
                  <SmileFilled style={{ color: 'white' }} />
                ) : (
                  <SmileOutlined style={{ color: 'white' }} />
                )}
              </span>
            )}
            {!isEditing && <Speed />}
            {!blockActions && isEditable && (
              <Scissors isEditing={isEditing} onEditionStarted={() => setIsEditing(true)} />
            )}
            {isEditable && !isEditing && <Download onClick={download} />}
            {!blockActions && isEditing && (
              <Button type="primary" size="small" onClick={markersEditionFinished}>
                Save
              </Button>
            )}
            {!blockActions && isEditing && (
              <Button size="small" onClick={markersEditionCanceled}>
                Cancel
              </Button>
            )}
          </LeftsideControls>
          <RightsideControl>
            <Fullscreen onClick={() => toggleFullscreen(!fullscreen)} />
            <VolumePanel />
          </RightsideControl>
        </ControlsContainer>
      </AllControls>
    </Container>
  );
};
